import { z } from 'zod';

import {
  cashMovementDirectionValues,
  cashFundingSourceTypeValues,
  cashMovementKindValues,
  cashMovementSourceTypeValues,
  type CashFundingSourceType,
  type CashMovementDirection,
  type CashMovementDto,
  type CashMovementKind,
  type CashMovementSourceType,
  type ListCashMovementsRequest,
  type ListCashMovementsResponse,
} from './cash-movement.dto.js';

const timestampSchema = z.iso.datetime();

export const CashMovementDirectionSchema: z.ZodType<CashMovementDirection> = z.enum(
  cashMovementDirectionValues,
);
export const CashMovementKindSchema: z.ZodType<CashMovementKind> = z.enum(cashMovementKindValues);
export const CashMovementSourceTypeSchema: z.ZodType<CashMovementSourceType> = z.enum(
  cashMovementSourceTypeValues,
);
export const CashFundingSourceTypeSchema: z.ZodType<CashFundingSourceType> = z.enum(
  cashFundingSourceTypeValues,
);

export const CashMovementDtoSchema: z.ZodType<CashMovementDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    cashLocationId: z.uuid(),
    cashSessionId: z.uuid().nullable(),
    transferId: z.uuid().nullable(),
    expenseId: z.uuid().nullable(),
    movementKind: CashMovementKindSchema,
    direction: CashMovementDirectionSchema,
    amount: z.number().int().positive(),
    description: z.string().trim().min(1).max(500).nullable(),
    isReversal: z.boolean(),
    reversalOfMovementId: z.uuid().nullable(),
    sourceType: CashMovementSourceTypeSchema.nullable(),
    sourceId: z.uuid().nullable(),
    fundingSourceCategoryId: z.uuid().nullable().optional(),
    fundingSourceCategoryCodeSnapshot: z.string().trim().min(1).max(80).nullable().optional(),
    fundingSourceCategoryDisplayNameSnapshot: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .nullable()
      .optional(),
    fundingSourceType: CashFundingSourceTypeSchema.nullable().optional(),
    fundingReference: z.string().trim().min(1).max(120).nullable().optional(),
    reasonCode: z.string().trim().min(1).max(100).nullable().optional(),
    varianceCaseId: z.uuid().nullable().optional(),
    varianceActionId: z.uuid().nullable().optional(),
    occurredAt: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((value, context) => {
    if (value.reasonCode === 'variance_writeoff') {
      context.addIssue({
        code: 'custom',
        message: 'variance_writeoff is not an active cash movement reason.',
        path: ['reasonCode'],
      });
    }
  });

export const ListCashMovementsRequestSchema: z.ZodType<ListCashMovementsRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  stationId: z.uuid().optional(),
  cashLocationId: z.uuid().optional(),
  cashSessionId: z.uuid().optional(),
  transferId: z.uuid().optional(),
  expenseId: z.uuid().optional(),
  movementKind: CashMovementKindSchema.optional(),
  direction: CashMovementDirectionSchema.optional(),
  sourceType: CashMovementSourceTypeSchema.optional(),
  sourceId: z.uuid().optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
});

export const ListCashMovementsResponseSchema: z.ZodType<ListCashMovementsResponse> = z.object({
  items: z.array(CashMovementDtoSchema),
});
